/**
 * Disposition d'un container proposal → disposition d'un Bloc web.
 *
 * Le proposal sait depuis peu empiler des lignes dans une colonne
 * (`docs/nested-rows.md`) : `container > colonnes > lignes > sous-colonnes >
 * box`. Le web a déjà la structure jumelle — `preset: 'nested'`, colonnes
 * principales → lignes → cellules (`proposalSectionNested.js`) — où **l'id
 * d'une cellule est la clé de sa zone Puck**.
 *
 * Ce module fait la correspondance, et rend en une passe la disposition **et**
 * le contenu de chaque cellule, pour que le parser n'ait pas à reparcourir
 * l'arbre.
 */

import { legacyFullSpansToPartial } from './proposalSectionColumns';

/** Douzièmes occupés par une colonne, depuis sa fraction `size`. */
function spanOf(column) {
  return Math.max(1, Math.min(12, Math.round((column?.size ?? 1) * 12) || 12));
}

/**
 * Cellule supplémentaire d'une colonne éclatée en lignes. La première garde
 * l'id de la colonne source : son contenu ne change donc pas de zone d'une
 * génération à l'autre. Les suivantes en dérivent — **déterministe**, un id
 * tiré au hasard rebattrait les clés de zone à chaque régénération.
 */
function splitCellId(columnId, index) {
  return index === 0 ? columnId : `${columnId}--r${index}`;
}

/**
 * Toutes les boxes portées par une colonne et ses descendants, dans l'ordre du
 * document. Sert au garde-fou de profondeur : le modèle web s'arrête à deux
 * niveaux, mais rien ne doit disparaître si les données en portent trois.
 */
function collectBoxesDeep(columnId, { columnById, rowById, boxesOf }) {
  const column = columnById.get(columnId);
  const rowIds = column?.rowContainerIds ?? [];
  // Une colonne à lignes peut malgré tout porter des boxes en direct :
  // `mutateSetColumnRows` (store/containers.js) ne remonte que la *première*
  // box dans la sous-colonne créée, les suivantes restent attachées ici.
  const own = boxesOf(columnId);
  if (!rowIds.length) return own;

  const nested = rowIds.flatMap((rowId) => (
    (rowById.get(rowId)?.columnsIds ?? [])
      .flatMap((subId) => collectBoxesDeep(subId, { columnById, rowById, boxesOf }))
  ));
  return [...own, ...nested];
}

/** Lignes web d'une colonne proposal découpée en lignes. */
function rowsOfRowedColumn(column, ctx) {
  const { columnById, rowById, boxesOf } = ctx;
  const cells = [];

  const rows = (column.rowContainerIds ?? []).map((rowId, rowIndex) => {
    const row = rowById.get(rowId);
    const subColumns = (row?.columnsIds ?? [])
      .map((id) => columnById.get(id))
      .filter(Boolean);

    subColumns.forEach((subColumn) => {
      cells.push({
        id: subColumn.id,
        // Descente récursive : une sous-colonne qui porterait elle-même des
        // lignes n'a pas de troisième niveau où aller côté web, son contenu
        // est donc aplati ici plutôt que perdu.
        boxes: collectBoxesDeep(subColumn.id, ctx),
      });
    });

    return {
      id: `row-${column.id}-${rowIndex}`,
      columnSpans: legacyFullSpansToPartial(subColumns.map(spanOf)),
      columns: subColumns.map((subColumn) => ({ id: subColumn.id })),
    };
  });

  // Les boxes restées sur la colonne porteuse rejoignent la première cellule :
  // elles la précédaient dans le document.
  const orphans = boxesOf(column.id);
  if (orphans.length && cells.length) {
    cells[0].boxes = [...orphans, ...cells[0].boxes];
  }

  return { rows, cells };
}

/** Lignes web d'une colonne classique : une par box au-delà de la première. */
function rowsOfStackedColumn(column, boxes) {
  const cells = boxes.map((box, index) => ({
    id: splitCellId(column.id, index),
    boxes: [box],
  }));

  return {
    rows: cells.map((cell, index) => ({
      id: `row-${column.id}-${index}`,
      columnSpans: [],
      columns: [{ id: cell.id }],
    })),
    cells,
  };
}

/**
 * Disposition d'un container, et contenu de chacune de ses cellules.
 *
 * Reste en `preset: 'row'` — donc strictement ce que le parser produisait
 * avant — tant qu'aucune colonne ne porte de lignes ni plus d'une box.
 *
 * @param {object} params
 * @param {object} params.container       container proposal
 * @param {Array}  params.columns         toutes les colonnes du document
 * @param {Array}  params.rowContainers   toutes les lignes du document
 * @param {(columnId: string) => Array} params.boxesOf
 *        boxes d'une colonne, déjà filtrées et triées par le parser
 * @returns {{ preset: 'row'|'nested', columnIds?: string[], columnSpans?: Array,
 *             nested?: object, cells: Array<{ id: string, boxes: Array }> } | null}
 */
export function resolveContainerLayout({
  container,
  columns = [],
  rowContainers = [],
  boxesOf,
}) {
  const columnById = new Map(columns.map((c) => [c.id, c]));
  const rowById = new Map(rowContainers.map((r) => [r.id, r]));
  const ctx = { columnById, rowById, boxesOf };

  const topColumns = (container?.columnsIds ?? [])
    .map((id) => columnById.get(id))
    .filter(Boolean);

  if (topColumns.length === 0) return null;

  const isRowed = (column) => !!column.rowContainerIds?.length;
  const boxesByColumn = new Map(topColumns.map((c) => [c.id, boxesOf(c.id)]));

  const needsNested = topColumns.some(
    (c) => isRowed(c) || (boxesByColumn.get(c.id) || []).length > 1,
  );

  if (!needsNested) {
    return {
      preset: 'row',
      columnIds: topColumns.map((c) => c.id),
      columnSpans: legacyFullSpansToPartial(topColumns.map(spanOf)),
      cells: topColumns.map((c) => ({ id: c.id, boxes: boxesByColumn.get(c.id) || [] })),
    };
  }

  const cells = [];
  const mainColumns = topColumns.map((column) => {
    const built = isRowed(column)
      ? rowsOfRowedColumn(column, ctx)
      : rowsOfStackedColumn(column, boxesByColumn.get(column.id) || []);
    cells.push(...built.cells);

    // Une colonne vide garde une cellule : sans elle, la colonne principale
    // n'aurait aucune zone et disparaîtrait de la grille.
    const rows = built.rows.length
      ? built.rows
      : [{ id: `row-${column.id}-0`, columnSpans: [], columns: [{ id: column.id }] }];
    if (!built.rows.length) cells.push({ id: column.id, boxes: [] });

    return { id: `mc-${column.id}`, rows };
  });

  return {
    preset: 'nested',
    nested: {
      mainColumnSpans: legacyFullSpansToPartial(topColumns.map(spanOf)),
      mainColumns,
    },
    cells,
  };
}
